import React, { useEffect, useRef, useState } from 'react';
import { LatexText, LatexParagraphs } from '../SoruKarti';
import { dersLabel, kazanimSozleri } from '../../lib/dersler';
import { puanRenk, puanSeviyeAdi } from '../../lib/zorlukSeviye';

const HARFLER = ['A', 'B', 'C', 'D', 'E'];

// Şekil tembel yüklenir (GET /api/musteri/sorular/:no/sekil): kart ekrana girince; IntersectionObserver
// yoksa hemen. SVG <img> içinde basılır — sayfada 20 TikZ SVG'si yan yana dururken glyph/clip
// kimlikleri çakışmasın (satır içi SVG'de aynı `g0-1` başka sorunun harfini çizerdi).
function Sekil({ no, api }) {
  const kutu = useRef(null);
  const [durum, setDurum] = useState({ svg: null, hata: '', yukleniyor: false });
  const [gorundu, setGorundu] = useState(false);

  useEffect(() => {
    if (gorundu || !kutu.current) return undefined;
    if (typeof window === 'undefined' || !('IntersectionObserver' in window)) { setGorundu(true); return undefined; }
    const io = new window.IntersectionObserver((girdiler) => {
      if (girdiler.some((g) => g.isIntersecting)) { setGorundu(true); io.disconnect(); }
    }, { rootMargin: '200px' });
    io.observe(kutu.current);
    return () => io.disconnect();
  }, [gorundu]);

  useEffect(() => {
    if (!gorundu) return undefined;
    let iptal = false;
    setDurum({ svg: null, hata: '', yukleniyor: true });
    api.sekil(no)
      .then((d) => { if (!iptal) setDurum({ svg: d.svg || null, hata: d.svg ? '' : 'Şekil alınamadı', yukleniyor: false }); })
      .catch((e) => { if (!iptal) setDurum({ svg: null, hata: e.message || 'Şekil alınamadı', yukleniyor: false }); });
    return () => { iptal = true; };
  }, [gorundu, no, api]);

  return (
    <div ref={kutu} data-musteri-sekil={no} className="my-3 text-center">
      {durum.svg ? (
        <img alt={`#${no} şekli`} src={`data:image/svg+xml;charset=utf-8,${encodeURIComponent(durum.svg)}`}
          className="inline-block max-w-full h-auto max-h-[260px]" />
      ) : durum.hata ? (
        <div className="text-xs text-orange-700">Şekil gösterilemedi: {durum.hata}</div>
      ) : (
        <div className="text-xs text-gray-400 py-6">Şekil yükleniyor…</div>
      )}
    </div>
  );
}

/**
 * Müşteriye gösterilen soru kartı. Alanlar backend routes/musteri.js `musteriSoru` çıktısı: metin,
 * şıklar, şekil, doğru cevap, çözüm (kullanıcı kararı 2026-10-26: "cevap ve çözüm de görünsün").
 * Öğretmen yorumu / iç not / sayı YOK. `onSepet` verilmezse (sipariş ayrıntısı) düğme çıkmaz.
 */
export default function MusteriSoruKarti({ soru, api, sepette = false, onSepet = null, mesgul = false }) {
  const [cozumAcik, setCozumAcik] = useState(false);
  const soz = kazanimSozleri(soru.mufredat);
  const siklar = soru.siklar || {};
  const seviye = puanSeviyeAdi(soru.zorluk_puan);
  const uyeler = Array.isArray(soru.baglam_uyeleri) ? soru.baglam_uyeleri : null;
  const secilemez = Boolean(soru.gorsel_hata);

  return (
    <article data-musteri-soru={soru.soru_no} className="flex flex-col h-full bg-white rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-shadow overflow-hidden">
      <header className="flex flex-wrap items-center gap-1.5 px-4 py-2 bg-gray-50 border-b border-gray-200 text-xs">
        <span className="font-mono font-bold text-gray-800">#{soru.soru_no}</span>
        <span className="font-semibold text-gray-700">{dersLabel(soru.ders)}</span>
        {seviye && (
          <span className="px-1.5 py-0.5 rounded text-white font-semibold" style={{ backgroundColor: puanRenk(soru.zorluk_puan) }}
            title={`Öğretmen zorluk puanı ${soru.zorluk_puan}/10`}>{seviye}</span>
        )}
        <span className="px-1.5 py-0.5 rounded bg-gray-200 text-gray-600">{soru.mufredat === 'Maarif_2024' ? 'Maarif' : 'MEB 2018'}</span>
        {uyeler && (
          <span data-baglam-rozet className="px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 font-semibold"
            title="Bağlam temelli soru grubu — ortak metne bağlı sorular birlikte verilir">
            Bağlam · {uyeler.length} soru birlikte ({uyeler.map((n) => `#${n}`).join(', ')})
          </span>
        )}
      </header>
      {soru.konu && (
        <div className="px-4 pt-2 text-xs text-gray-600 line-clamp-1" title={`${soru.konu}${soru.alt_konu ? ` › ${soru.alt_konu}` : ''}`}>
          {soru.konu}{soru.alt_konu ? ` › ${soru.alt_konu}` : ''}
        </div>
      )}
      {soru.kazanim_kodu && (
        <div className="px-4 pt-1 text-[11px] text-gray-500 line-clamp-2" title={soru.kazanim_adi || undefined}>
          {soz.Ad}: <span className="font-mono text-blue-700">{soru.kazanim_kodu}</span>{soru.kazanim_adi ? ` — ${soru.kazanim_adi}` : ''}
        </div>
      )}
      <div className="px-4 pt-3 text-[15px] text-gray-900 font-serif">
        {soru.soru_metni_latex ? <LatexParagraphs>{soru.soru_metni_latex}</LatexParagraphs> : <span className="italic text-gray-400">(soru metni yok)</span>}
        {soru.gorsel && !secilemez && <Sekil no={soru.soru_no} api={api} />}
      </div>
      <ol className="px-4 py-3 space-y-1.5">
        {HARFLER.filter((h) => siklar[h] != null).map((h) => {
          const dogru = cozumAcik && soru.dogru_cevap === h;
          return (
            <li key={h} data-sik={h} className={`flex gap-2 px-3 py-1.5 rounded-lg border text-sm font-serif ${dogru ? 'border-green-500 bg-green-50 text-green-900' : 'border-gray-200 bg-gray-50 text-gray-800'}`}>
              <span className="font-bold">{h})</span>
              <span className="flex-1"><LatexText>{siklar[h]}</LatexText></span>
              {dogru && <span className="text-green-700 font-bold">✓</span>}
            </li>
          );
        })}
      </ol>
      {cozumAcik && (
        <div data-cozum className="mx-4 mb-3 p-3 rounded-lg border border-blue-200 bg-blue-50 text-sm text-gray-800 font-serif space-y-2">
          <div className="text-xs font-bold text-blue-800 font-sans">Doğru cevap: {soru.dogru_cevap || '—'}</div>
          {soru.cozum_adimlari_latex && <LatexParagraphs>{soru.cozum_adimlari_latex}</LatexParagraphs>}
          {soru.pratik_cozum && (
            <div>
              <div className="text-xs font-bold text-blue-800 font-sans">Pratik çözüm</div>
              <LatexParagraphs>{soru.pratik_cozum}</LatexParagraphs>
            </div>
          )}
        </div>
      )}
      <footer className="mt-auto flex flex-wrap items-center gap-2 px-4 py-2 border-t border-gray-100 bg-gray-50/50">
        <button type="button" data-cozum-dugme aria-expanded={cozumAcik} onClick={() => setCozumAcik((a) => !a)}
          className="px-2.5 py-1 rounded border border-gray-300 bg-white text-xs text-gray-700 hover:bg-gray-50">
          {cozumAcik ? 'Cevap ve çözümü gizle' : 'Cevap ve çözüm'}
        </button>
        {secilemez && <span className="text-xs text-orange-700">⚠ {soru.gorsel_hata} — seçilemez</span>}
        {onSepet && (
          <button type="button" data-sepet-dugme={soru.soru_no} disabled={mesgul || (secilemez && !sepette)}
            onClick={() => onSepet(soru)}
            className={`ml-auto px-3 py-1 rounded text-xs font-semibold disabled:opacity-50 ${sepette
              ? 'bg-green-100 text-green-800 border border-green-300 hover:bg-green-200'
              : 'bg-blue-600 text-white hover:bg-blue-700'}`}>
            {sepette ? '✓ Sepette (çıkar)' : uyeler ? `Sepete ekle (${uyeler.length} soru)` : 'Sepete ekle'}
          </button>
        )}
      </footer>
    </article>
  );
}
